import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CardMedia,
  IconButton,
  Button,
  Alert,
  Snackbar,
  CircularProgress,
  Tabs,
  Tab,
  TextField,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
} from '@mui/material';
import {
  Delete,
  Download,
  Refresh,
  Upload,
  Search,
  FilterList,
  ViewList,
  ViewModule,
  Sort,
} from '@mui/icons-material';
import { MediaManager } from './components/MediaManager';
import { MediaPreview } from './components/MediaPreview';
import { uploadApi } from '../services/api';
import { MediaItem } from '../types/upload';
import { getStaticFileUrl } from '../utils/config';

interface FileItem extends MediaItem {}

export const FileManager: React.FC = () => {
  const [files, setFiles] = useState<FileItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<'all' | 'image' | 'video'>('all');
  const [sortBy, setSortBy] = useState<'date' | 'name' | 'size'>('date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    loadFiles();
  }, [filterType, sortBy, sortOrder]);

  const loadFiles = async () => {
    setLoading(true);
    try {
      const result = await uploadApi.getMediaList({
        type: filterType === 'all' ? undefined : filterType,
        sortBy,
        sortOrder,
      });
      setFiles(result.mediaList || []);
    } catch (error) {
      console.error('파일 목록 로드 실패:', error);
      setErrorMessage('파일 목록을 불러오는데 실패했습니다.');
      setShowError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = (fileUrl: string) => {
    // 새 파일 추가 로직
    const newFile: FileItem = {
      id: Date.now().toString(),
      name: fileUrl.split('/').pop() || 'unknown',
      url: fileUrl,
      type: fileUrl.includes('images') ? 'image' : 'video',
      size: 0,
      uploadDate: new Date().toISOString().split('T')[0],
      description: '',
      tags: [],
    };
    setFiles(prev => [newFile, ...prev]);
    setShowSuccess(true);
  };

  const handleFileUpdate = (updatedFile: FileItem) => {
    setFiles(prev => 
      prev.map(file => 
        file.id === updatedFile.id ? updatedFile : file
      )
    );
    setShowSuccess(true);
  };

  const handleFileDelete = async (fileId: string) => {
    try {
      const file = files.find(f => f.id === fileId);
      if (!file) return;
      
      // 파일 URL에서 타입과 파일명 추출
      const urlParts = file.url.split('/');
      const type = urlParts[urlParts.length - 2]; // images 또는 videos
      const filename = urlParts[urlParts.length - 1];
      
      await uploadApi.deleteFile(type, filename);
      setFiles(prev => prev.filter(file => file.id !== fileId));
      setShowSuccess(true);
    } catch (error) {
      console.error('파일 삭제 실패:', error);
      setErrorMessage('파일 삭제에 실패했습니다.');
      setShowError(true);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // 필터링 및 정렬된 파일 목록
  const filteredAndSortedFiles = files
    .filter(file => {
      const matchesSearch = file.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           (file.description && file.description.toLowerCase().includes(searchTerm.toLowerCase()));
      const matchesType = filterType === 'all' || file.type === filterType;
      return matchesSearch && matchesType;
    })
    .sort((a, b) => {
      let comparison = 0;
      
      switch (sortBy) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'size':
          comparison = a.size - b.size;
          break;
        case 'date':
        default:
          comparison = new Date(a.uploadDate).getTime() - new Date(b.uploadDate).getTime();
          break;
      }
      
      return sortOrder === 'asc' ? comparison : -comparison;
    });

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom>
        파일 관리
      </Typography>
      
      <Paper sx={{ p: 3, mb: 3 }}>
        {/* 검색 및 필터 섹션 */}
        <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="파일명 또는 설명으로 검색..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
            />
          </Grid>
          
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>파일 타입</InputLabel>
              <Select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value as 'all' | 'image' | 'video')}
                label="파일 타입"
              >
                <MenuItem value="all">전체</MenuItem>
                <MenuItem value="image">이미지</MenuItem>
                <MenuItem value="video">비디오</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>정렬 기준</InputLabel>
              <Select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as 'date' | 'name' | 'size')}
                label="정렬 기준"
              >
                <MenuItem value="date">업로드 날짜</MenuItem>
                <MenuItem value="name">파일명</MenuItem>
                <MenuItem value="size">파일 크기</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}
              startIcon={<Sort />}
            >
              {sortOrder === 'asc' ? '오름차순' : '내림차순'}
            </Button>
          </Grid>
          
          <Grid item xs={12} md={2}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant={viewMode === 'grid' ? 'contained' : 'outlined'}
                onClick={() => setViewMode('grid')}
                startIcon={<ViewModule />}
              >
                그리드
              </Button>
              <Button
                variant={viewMode === 'list' ? 'contained' : 'outlined'}
                onClick={() => setViewMode('list')}
                startIcon={<ViewList />}
              >
                목록
              </Button>
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ mb: 3 }} />

        {/* 통계 정보 */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h4" color="primary">
                  {files.length}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  전체 파일
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h4" color="primary">
                  {files.filter(f => f.type === 'image').length}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  이미지 파일
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h4" color="primary">
                  {files.filter(f => f.type === 'video').length}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  비디오 파일
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h4" color="primary">
                  {formatFileSize(files.reduce((sum, file) => sum + file.size, 0))}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  총 용량
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Divider sx={{ mb: 3 }} />

        {/* 파일 목록 */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : filteredAndSortedFiles.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" color="textSecondary" gutterBottom>
              파일이 없습니다
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {searchTerm || filterType !== 'all' ? '검색 조건을 변경해보세요.' : '새로운 파일을 업로드해보세요.'}
            </Typography>
          </Box>
        ) : viewMode === 'grid' ? (
          <Grid container spacing={2}>
            {filteredAndSortedFiles.map((file) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={file.id}>
                <Card>
                  <CardContent sx={{ p: 1 }}>
                    <MediaPreview
                      src={file.url}
                      alt={file.name}
                      title={file.name}
                      type={file.type}
                      width="100%"
                      height={150}
                      showPreviewButton={false}
                    />
                    
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="subtitle2" noWrap>
                        {file.name}
                      </Typography>
                      {file.description && (
                        <Typography variant="caption" color="textSecondary" noWrap>
                          {file.description}
                        </Typography>
                      )}
                      <Typography variant="caption" color="textSecondary" display="block">
                        {formatFileSize(file.size)} • {file.uploadDate}
                      </Typography>
                      
                      {file.tags && file.tags.length > 0 && (
                        <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {file.tags.slice(0, 2).map((tag, index) => (
                            <Chip
                              key={index}
                              label={tag}
                              size="small"
                              variant="outlined"
                            />
                          ))}
                          {file.tags.length > 2 && (
                            <Chip
                              label={`+${file.tags.length - 2}`}
                              size="small"
                              variant="outlined"
                            />
                          )}
                        </Box>
                      )}
                    </Box>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                      <IconButton
                        size="small"
                        onClick={() => window.open(getStaticFileUrl(file.url), '_blank')}
                      >
                        <Download />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleFileDelete(file.id)}
                      >
                        <Delete />
                      </IconButton>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Box>
            {filteredAndSortedFiles.map((file) => (
              <Card key={file.id} sx={{ mb: 2 }}>
                <CardContent>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={3}>
                      <MediaPreview
                        src={file.url}
                        alt={file.name}
                        title={file.name}
                        type={file.type}
                        width={120}
                        height={80}
                        showPreviewButton={false}
                      />
                    </Grid>
                    
                    <Grid item xs={12} sm={3}>
                      <Typography variant="subtitle2" gutterBottom>
                        {file.name}
                      </Typography>
                      {file.description && (
                        <Typography variant="caption" color="textSecondary">
                          {file.description}
                        </Typography>
                      )}
                    </Grid>
                    
                    <Grid item xs={12} sm={2}>
                      <Chip
                        label={file.type === 'image' ? '이미지' : '비디오'}
                        size="small"
                        color={file.type === 'image' ? 'primary' : 'secondary'}
                      />
                    </Grid>
                    
                    <Grid item xs={12} sm={2}>
                      <Typography variant="body2">
                        {formatFileSize(file.size)}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {file.uploadDate}
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={12} sm={2}>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <IconButton
                          size="small"
                          onClick={() => window.open(getStaticFileUrl(file.url), '_blank')}
                        >
                          <Download />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleFileDelete(file.id)}
                        >
                          <Delete />
                        </IconButton>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            ))}
          </Box>
        )}
      </Paper>

      {/* 미디어 매니저 섹션 */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>
          미디어 관리
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
          이미지와 영상을 업로드하고 관리할 수 있습니다.
        </Typography>
        
        <MediaManager
          onMediaSelect={(media) => {
            console.log('선택된 미디어:', media);
          }}
          onMediaUpdate={handleFileUpdate}
          onMediaDelete={handleFileDelete}
          multiple={false}
          showUpload={true}
          showPreview={true}
          showEdit={true}
          showDelete={true}
          filterType="all"
        />
      </Paper>

      {/* 성공 알림 */}
      <Snackbar
        open={showSuccess}
        autoHideDuration={3000}
        onClose={() => setShowSuccess(false)}
      >
        <Alert severity="success" onClose={() => setShowSuccess(false)}>
          작업이 성공적으로 완료되었습니다.
        </Alert>
      </Snackbar>

      {/* 에러 알림 */}
      <Snackbar
        open={showError}
        autoHideDuration={6000}
        onClose={() => setShowError(false)}
      >
        <Alert severity="error" onClose={() => setShowError(false)}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
}; 